import { useState, useEffect } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";

// Components
import Upcoming from "../messages/Upcoming";
import History from "../messages/History";
import Failed from "../messages/Failed";
import Header from "./Header";
import Joyride from "react-joyride";

// Theme
import { tokens } from "../../theme";

// Context
import { useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "../utils/Utils";

export default function Messages() {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [value, setValue] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Tutorial
  const [runTutorial, setRunTutorial] = useState(false);
  const [stepIndex, setStepIndex] = useState(-1);

  const steps = [
    {
      target: ".message-modal-contact-list",
      content: t("messages.tutorial.step2"),
      placement: "top",
    },
    {
      target: ".message-modal-submit",
      content: t("messages.tutorial.step3"),
    },
    {
      target: ".messages-tabs",
      content: t("messages.tutorial.step4"),
    },
    {
      target: ".pro-sidebar",
      content: t("messages.tutorial.step5"),
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { action, index, type, status } = data;

    if (status === "finished" || status === "skipped") {
      setRunTutorial(false);
      setStepIndex(0);
      navigate("/dashboard/messages");
    } else if (action === "prev" && index !== 0 && type === "step:after") {
      setStepIndex(index - 1); // Return to a previous step
    } else if (action === "next" && type === "step:after") {
      setStepIndex(index + 1); // Advance to next step
    } else if (action === "close") {
      setRunTutorial(false); // Close the tour
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get("tab");
    setRunTutorial(queryParams.get("runTutorial") === "true");
    setTimeout(() => {
      setStepIndex(0);
    }, 1000);

    trackEvent("messages-tab_clicked", { tab });

    if (tab === "history") {
      setValue(1);
    } else if (tab === "failed") {
      setValue(2);
    } else {
      setValue(0);
    }
  }, [location.search]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1600px", mx: "auto" }}>
      <Joyride
        steps={steps}
        run={runTutorial}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        disableOverlay={true}
        continuous
        disableBeacon
        styles={{
          options: {
            zIndex: 10000,
          },
        }}
        locale={{
          back: t("messages.tutorial.back"),
          close: t("messages.tutorial.close"),
          last: t("messages.tutorial.last"),
          next: t("messages.tutorial.next"),
          skip: t("messages.tutorial.skip"),
        }}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Header title={t("messages.title")} subtitle={t("messages.subtitle")} />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Tabs
          value={value}
          onChange={handleChange}
          className="messages-tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: "48px",
            bgcolor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.03)",
            borderRadius: "12px",
            p: 0.5,
            display: "flex",
            maxWidth: "100%",
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "600",
              fontSize: "0.95rem",
              minHeight: "40px",
              borderRadius: "8px",
              color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681",
              transition: "all 0.3s ease",
              px: 4,
              "&.Mui-selected": {
                color: theme.palette.mode === 'dark' ? "#fff" : "#141b2d",
                bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.1)" : "#fff",
                boxShadow: theme.palette.mode === 'dark' ? "none" : "0 2px 8px rgba(0,0,0,0.05)",
              },
              "&:hover:not(.Mui-selected)": {
                color: theme.palette.mode === 'dark' ? "#fff" : "#141b2d",
                bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.5)",
              },
            },
          }}
        >
          <Tab disableRipple label={t("messages.upcoming")} />
          <Tab disableRipple label={t("messages.history")} />
          <Tab disableRipple label={t("messages.failed")} />
        </Tabs>
      </Box>

      {/* Content wrapper with glassmorphism */}
      <Box
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? "rgba(20, 27, 45, 0.7)" : "#ffffff",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          boxShadow: theme.palette.mode === 'dark' ? "0 8px 32px 0 rgba(0, 0, 0, 0.3)" : "0 8px 32px 0 rgba(31, 38, 135, 0.05)",
          border: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          p: { xs: 2, md: 3 },
          minHeight: "500px"
        }}
      >
        {value === 0 && <Upcoming runTutorial={runTutorial} />}
        {value === 1 && <History />}
        {value === 2 && <Failed />}
      </Box>
    </Box>
  );
}
